import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { formatRupees } from "../../users";
import { PAYMENT_STATUS_STYLES, PLAN_DURATION } from "../paymentStyles";
import type { Payment } from "../types/revenue.types";

const COLUMNS = [
  { heading: "Sl", width: "w-[7%]" },
  { heading: "Client name", width: "w-[22%]" },
  { heading: "Place", width: "w-[17%]" },
  { heading: "Plan", width: "w-[15%]" },
  { heading: "Amount", width: "w-[12%]" },
  { heading: "Duration", width: "w-[12%]" },
  { heading: "Status", width: "w-[15%]" },
];

interface PaymentsTableProps {
  payments: Payment[];
  /**
   * What the first row of this page is numbered.
   *
   * Passed in rather than counted here, because the Sl column numbers the list
   * and not the page: row one of page two is 11, and a table that only knows
   * its own ten rows would start again at 01.
   */
  startIndex: number;
  /** Stands in for the body when the list comes back with no rows. */
  empty: EmptyStateProps;
  /**
   * Where the rows are. While they are on their way the body says nothing — no
   * rows and no message — and only a load slow enough to notice shimmers, as
   * one block the size of the table. Rows already in hand are left alone
   * either way: a table with something to show goes on showing it.
   */
  phase?: LoadPhase;
  /** Opens the account behind the row. The whole row is the target — a name is
   *  a four-pixel-tall thing to aim at in a fifty-pixel row. */
  onSelect?: (payment: Payment) => void;
}

/** Every payment, newest first — one page of them. */
const PaymentsTable: React.FC<PaymentsTableProps> = ({
  payments,
  startIndex,
  empty,
  phase = "ready",
  onSelect,
}) => {
  if (phase !== "ready" && payments.length === 0) {
    // The table's own area, standing in for the whole list. Hidden from screen
    // readers with the rest of the placeholders; the card says it is loading
    // in words.
    return (
      <div className="flex-1 min-h-0 p-4 sm:p-6">
        <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
      </div>
    );
  }

  const cellClasses = "px-4 sm:px-6 py-4 text-[14px] text-ink";

  return (
    <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
      <table
        className={`w-full table-fixed border-collapse text-left min-w-[860px] ${
          payments.length === 0 ? "h-full" : ""
        }`}
      >
        <colgroup>
          {COLUMNS.map((column) => (
            <col key={column.heading} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.heading}
                scope="col"
                className="sticky top-0 z-10 bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {column.heading}
              </th>
            ))}
          </tr>
        </thead>

        {payments.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {payments.map((payment, index) => (
              <tr
                key={payment.id}
                onClick={() => onSelect?.(payment)}
                className={`border-b border-line last:border-0 transition-colors ${
                  onSelect ? "hover:bg-[#F8FEFF] cursor-pointer" : ""
                }`}
              >
                {/* Padded to two digits so the column reads as a column rather
                    than as a ragged left edge. */}
                <td className={`${cellClasses} text-ink-muted tabular-nums`}>
                  {String(startIndex + index).padStart(2, "0")}
                </td>
                <td className={`${cellClasses} truncate`}>
                  {payment.accountName}
                </td>
                <td className={`${cellClasses} truncate`}>{payment.place}</td>
                <td className={`${cellClasses} truncate`}>{payment.plan}</td>
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {formatRupees(payment.amount)}
                </td>
                {/* Worked out from the plan — see `PLAN_DURATION`. */}
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {PLAN_DURATION[payment.plan]}
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      PAYMENT_STATUS_STYLES[payment.status]
                    }`}
                  >
                    {payment.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default PaymentsTable;
